import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicTable, { SelectColumnFilter } from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import {
  DeleteButton,
  EditButton,
  PurpleButton,
} from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import {
  getDeliverablesByProjectId,
  deleteDeliverable,
  DeliverableResponse,
} from '@/services/DeliverablesService';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useAuth } from '@/context/AuthContext';
import { AssignedTask } from '@/enums/AssignedTask';
import { UserRoles } from '@/enums/UsersRole';

const userTimeZone = moment.tz.guess();

function Deliverables() {
  const { id } = useParams();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();

  const { user } = useAuth();

  const isAllowed = useMemo(
    () =>
      user?.assignedTask == AssignedTask.DeliverablesFollowUp ||
      user?.role == UserRoles.Admin,
    [user]
  );
  console.log('🚀 ~ Deliverables ~ isAllowed:', isAllowed);
  const { t } = useTranslation();

  const ActionButtonsCell = ({ value }: any) => (
    <div>
      <DeleteButton
        onClick={() => {
          setSelectedRow(value);
          setIsModalVisible(true);
        }}
      >
        {t('Deliverables.deleteButton')}
      </DeleteButton>
      <EditButton
        type="button"
        onClick={() => {
          navigate(`${value}`);
        }}
      >
        {t('Deliverables.editButton')}
      </EditButton>
    </div>
  );

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteDeliverable.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('Deliverables.actionsColumn'),
        accessor: 'id',
        Cell: ({ cell: { value } }: any) => <ActionButtonsCell value={value} />,
      },
      {
        Header: t('Deliverables.numberColumn'),
        accessor: 'deliverableNumber',
      },

      {
        Header: t('Deliverables.descriptionColumn'),
        accessor: 'description',
      },
      {
        Header: t('Deliverables.startDateColumn'),
        accessor: 'plannedStartDate',
        Cell: ({ cell: { value } }: any) => {
          const formattedValue = moment(value).format('YYYY-MM-DD');
          return (
            <input
              type="text"
              className="min-w-40 dark:bg-boxdark-2 dark:text-bodydark"
              value={formattedValue}
              readOnly
            />
          );
        },
      },
      {
        Header: t('Deliverables.endDateColumn'),
        accessor: 'plannedEndDate',
        Cell: ({ cell: { value } }: any) => {
          const formattedValue = moment(value).format('YYYY-MM-DD');
          return (
            <input
              type="text"
              className="min-w-40 dark:bg-boxdark-2 dark:text-bodydark"
              value={formattedValue}
              readOnly
            />
          );
        },
      },
      {
        Header: t('Deliverables.progressStatusColumn'),
        accessor: 'progressStatus',
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: deliverables,
  } = useQuery<DeliverableResponse | undefined>(
    ['deliverables', pageIndex, pageSize, search],
    () =>
      getDeliverablesByProjectId({
        projectId: id!,
        page: pageIndex,
        take: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
      enabled: id !== null,
    }
  );

  const handleDeleteDeliverable = useMutation(
    async (id: string) => {
      await deleteDeliverable(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'deliverables',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((deliverables?.count ?? 0) / pageSize);
  }, [pageSize, deliverables]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={t('Deliverables.pageName')}
        addbtn={isAllowed}
        addRouter="new"
      />
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={deliverables?.count}
            data={deliverables?.deliverables ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default Deliverables;
